import { useState } from 'react';
import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../lib/auth-context.js';
import { useOrg } from '../lib/org-context.js';
import { ImpersonationBanner } from '../components/ImpersonationBanner.js';
import { formatUserName } from '@crm98/shared';

interface NavItem {
  to: string;
  label: string;
  icon: string;
}

const ADMIN_SUBNAV: NavItem[] = [
  { to: '/app/team', label: 'Team', icon: 'team' },
  { to: '/app/programs', label: 'Programs', icon: 'grid' },
  { to: '/app/settings', label: 'Settings', icon: 'cog' },
  { to: '/app/integrations', label: 'Integrations', icon: 'plug' },
];

export function CustomerLayout() {
  const { logout, userEmail, userFirstName, userLastName, isImpersonating, orgId: authOrgId, orgName: authOrgName } = useAuth();
  const { isAdmin, isPlatformMode, viewingOrgName, viewingOrgId, deselectOrg, userRole } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  const [showMenu, setShowMenu] = useState(false);
  const [showAdminDropdown, setShowAdminDropdown] = useState(false);

  const isAdminUser = userRole !== 'staff';

  // Check if current route is in the admin sub-nav
  const isAdminSection = ADMIN_SUBNAV.some(item => location.pathname.startsWith(item.to));

  // Header subtitle — show org name
  const displayOrgName = viewingOrgName ?? authOrgName;
  const subtitle = isAdmin && viewingOrgName
    ? `Viewing: ${viewingOrgName}`
    : displayOrgName ?? 'Lead Portal';

  // Org ID for the admin dropdown
  const currentOrgId = viewingOrgId ?? authOrgId;

  return (
    <div className="min-h-screen bg-gray-50 pb-16 md:pb-0">
      {/* Impersonation banner */}
      {isImpersonating && <ImpersonationBanner />}

      {/* Top header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-4xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            {/* Back button for admin viewing an org */}
            {isAdmin && !isPlatformMode && (
              <button
                onClick={() => {
                  deselectOrg();
                  navigate('/admin/orgs');
                }}
                className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center hover:bg-gray-200 transition-colors mr-1"
                title="Back to Accounts"
              >
                <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
              </button>
            )}
            <div className="w-8 h-8 bg-primary-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">97</span>
            </div>
            <div>
              <h1 className="font-semibold text-gray-900 text-sm leading-tight">CRM98</h1>
              <p className="text-xs text-gray-400 leading-tight truncate max-w-[200px]">{subtitle}</p>
            </div>
          </div>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-1">
            {/* Dashboard — all users */}
            <NavLink
              to="/app"
              end
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              Dashboard
            </NavLink>

            {/* Leads — all users */}
            <NavLink
              to="/app/leads"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              Leads
            </NavLink>

            {/* Communication — all users */}
            <NavLink
              to="/app/drip"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              Communication
            </NavLink>

            {/* Website — all users */}
            <NavLink
              to="/app/website"
              className={({ isActive }) =>
                `px-3 py-1.5 text-sm rounded-lg transition-colors ${
                  isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                }`
              }
            >
              Website
            </NavLink>

            {/* Admin dropdown — admin only */}
            {isAdminUser && (
              <div className="relative">
                <button
                  onClick={() => setShowAdminDropdown(!showAdminDropdown)}
                  className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1 ${
                    isAdminSection ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  Admin
                  <svg className={`w-3 h-3 transition-transform ${showAdminDropdown ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {showAdminDropdown && (
                  <>
                    <div className="fixed inset-0 z-30" onClick={() => setShowAdminDropdown(false)} />
                    <div className="absolute right-0 top-9 bg-white rounded-xl shadow-lg border border-gray-200 z-40 w-56 overflow-hidden py-1">
                      {ADMIN_SUBNAV.map(item => (
                        <NavLink
                          key={item.to}
                          to={item.to}
                          onClick={() => setShowAdminDropdown(false)}
                          className={({ isActive }) =>
                            `block px-4 py-2 text-sm transition-colors ${
                              isActive ? 'bg-primary-50 text-primary-700 font-medium' : 'text-gray-700 hover:bg-gray-50'
                            }`
                          }
                        >
                          {item.label}
                        </NavLink>
                      ))}
                      {currentOrgId && (
                        <div className="border-t border-gray-100 mt-1 pt-2 px-4 pb-2">
                          <p className="text-[10px] font-medium text-gray-400 uppercase tracking-wider mb-1">Org UUID</p>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(currentOrgId);
                              setShowAdminDropdown(false);
                            }}
                            className="w-full flex items-center gap-2 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                            title="Copy Org ID"
                          >
                            <span className="truncate font-mono">{currentOrgId}</span>
                            <svg className="w-3.5 h-3.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                            </svg>
                          </button>
                        </div>
                      )}
                    </div>
                  </>
                )}
              </div>
            )}
          </nav>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowMenu(!showMenu)}
              className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
            >
              <svg className="w-4 h-4 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
              </svg>
            </button>
            {showMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowMenu(false)} />
                <div className="absolute right-0 top-10 bg-white rounded-xl shadow-lg border border-gray-200 z-50 w-56 overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100">
                    <p className="text-sm font-medium text-gray-900 truncate">{formatUserName({ firstName: userFirstName, lastName: userLastName, email: userEmail ?? '' })}</p>
                    {userRole && (
                      <p className="text-xs text-gray-500 mt-0.5 capitalize">{userRole.replace(/_/g, ' ')}</p>
                    )}
                  </div>
                  <div className="py-1">
                    <button
                      onClick={() => {
                        logout();
                        setShowMenu(false);
                      }}
                      className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      Sign out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-4 py-6">
        <Outlet />
      </main>

      {/* Mobile bottom nav */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-40">
        <div className="flex">
          {/* Dashboard — all users */}
          <NavLink
            to="/app"
            end
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 ${isActive ? 'text-primary-600' : 'text-gray-400'}`
            }
          >
            <NavIcon icon="dashboard" />
            <span className="text-xs mt-0.5 font-medium">Home</span>
          </NavLink>

          {/* Leads — all users */}
          <NavLink
            to="/app/leads"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 ${isActive ? 'text-primary-600' : 'text-gray-400'}`
            }
          >
            <NavIcon icon="people" />
            <span className="text-xs mt-0.5 font-medium">Leads</span>
          </NavLink>

          {/* Communication — all users */}
          <NavLink
            to="/app/drip"
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center py-2 ${isActive ? 'text-primary-600' : 'text-gray-400'}`
            }
          >
            <NavIcon icon="drip" />
            <span className="text-xs mt-0.5 font-medium">Comms</span>
          </NavLink>

          {/* Admin — admin only, highlights when on any admin sub-page */}
          {isAdminUser && (
            <NavLink
              to="/app/team"
              className={() =>
                `flex-1 flex flex-col items-center py-2 ${isAdminSection ? 'text-primary-600' : 'text-gray-400'}`
              }
            >
              <NavIcon icon="cog" />
              <span className="text-xs mt-0.5 font-medium">Admin</span>
            </NavLink>
          )}
        </div>
      </nav>
    </div>
  );
}

function NavIcon({ icon }: { icon: string }) {
  const paths: Record<string, string> = {
    dashboard:
      'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
    people:
      'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
    plug: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
    drip: 'M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
    cog: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z',
    team: 'M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z',
    grid: 'M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z',
  };

  const d = paths[icon] ?? paths['people']!;
  const isCog = icon === 'cog';

  return (
    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={d} />
      {isCog && (
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      )}
    </svg>
  );
}
